import React from 'react'
import {
  LEAD_STAGES,
  leadSourceNeedsDetail,
  type LeadManagementFormData,
  type LeadStage,
} from '../lib/leadHelpers'

interface LeadSource {
  id: string
  name: string
}

interface UserProfile {
  id: string
  first_name?: string
  last_name?: string
  email: string
}

interface LeadManagementFieldsProps {
  leadSources: LeadSource[]
  users: UserProfile[]
  value: LeadManagementFormData
  onChange: (patch: Partial<LeadManagementFormData>) => void
  showStage?: boolean
}

export function LeadManagementFields({
  leadSources,
  users,
  value,
  onChange,
  showStage = false,
}: LeadManagementFieldsProps) {
  const selectedSource = leadSources.find((s) => s.id === value.lead_source_id)
  const showSourceDetail = leadSourceNeedsDetail(selectedSource?.name)

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">Lead Source</label>
        <select
          value={value.lead_source_id}
          onChange={(e) => {
            const nextId = e.target.value
            const nextSource = leadSources.find((s) => s.id === nextId)
            onChange({
              lead_source_id: nextId,
              lead_source_other: leadSourceNeedsDetail(nextSource?.name)
                ? value.lead_source_other
                : '',
            })
          }}
          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
        >
          <option value="">Select Source</option>
          {leadSources.map((source) => (
            <option key={source.id} value={source.id}>
              {source.name}
            </option>
          ))}
        </select>
      </div>

      {showSourceDetail && (
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            {selectedSource?.name?.toLowerCase() === 'referral'
              ? 'Referral Name'
              : 'Please Specify'}
          </label>
          <input
            type="text"
            value={value.lead_source_other}
            onChange={(e) => onChange({ lead_source_other: e.target.value })}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            placeholder={
              selectedSource?.name?.toLowerCase() === 'referral'
                ? 'Who referred this lead?'
                : 'Describe the lead source'
            }
          />
        </div>
      )}

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">Assigned To</label>
        <select
          value={value.assigned_to}
          onChange={(e) => onChange({ assigned_to: e.target.value })}
          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
        >
          <option value="">Unassigned</option>
          {users.map((user) => (
            <option key={user.id} value={user.id}>
              {user.first_name} {user.last_name} ({user.email})
            </option>
          ))}
        </select>
      </div>

      {showStage && (
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">Stage</label>
          <select
            value={value.stage || 'new'}
            onChange={(e) => onChange({ stage: e.target.value as LeadStage })}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
          >
            {LEAD_STAGES.map((s) => (
              <option key={s.value} value={s.value}>
                {s.label}
              </option>
            ))}
          </select>
        </div>
      )}

      <div className={showStage && !showSourceDetail ? '' : 'md:col-span-2'}>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Date for Next Activity
        </label>
        <input
          type="date"
          value={value.next_activity_date}
          onChange={(e) => onChange({ next_activity_date: e.target.value })}
          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
        />
      </div>
    </div>
  )
}
